import React, { type ChangeEvent, useEffect, useState } from 'react';

import { Caption } from '@depop/web-thrift-ui/Typography';
import { TimerCountdownIcon } from '@depop/web-ui-kit/Icons/TimerCountdownIcon';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { ButtonExperiment } from '@/components/ButtonExperiment';
import { SuggestedOfferPriceButtons } from '@/components/MakeOfferModal/MakeOfferModalForm/SuggestedOfferPriceButtons';
import { OfferModalInput } from '@/components/OfferModalInput';
import { OfferModalProductSummary } from '@/components/OfferModalProductSummary';
import { type SuggestionOptionSelection } from '@/components/SuggestionOptionButtons';
import { OFFER_ERROR_TRANSLATION_KEYS } from '@/constants/offers';
import {
  RQ_BUYER_ACTIVE_OFFERS,
  RQ_X_REGION_CODE,
} from '@/modules/ReactQuery/cacheKeys';
import { useDebounce } from '@/modules/debounce/useDebounce';
import { isWebCrawler } from '@/modules/headers/isWebCrawler';
import { type AxiosCompatibleError } from '@/modules/http/types';
import { Stdv1Stat } from '@/modules/observability/constants';
import { recordMetric } from '@/modules/observability/metrics';
import {
  type OfferErrorResponse,
  type OfferPriceQuoteResponse,
  type CreateOfferRequest,
  type PriceQuoteRequestBody,
} from '@/modules/offers/buyers/aliases';
import {
  createOffer,
  createOfferPriceQuote,
} from '@/modules/offers/buyers/api';
import { isValidOfferValue } from '@/modules/offers/buyers/helpers/isValidOfferValue';
import { useOffersTracking } from '@/modules/offers/buyers/hooks/useOffersTracking';
import { type OfferProductPrice } from '@/modules/offers/buyers/types';
import {
  DEBOUNCE_PRICE_QUOTE_DELAY,
  GENERIC_ERROR_ID,
} from '@/modules/offers/constants';
import { type HeadlinePriceComponents } from '@/modules/price/aliases';

type Props = {
  currencySymbol: string;
  isAuthenticated: boolean;
  onOfferSent: () => void;
  productId: number;
  productImageHref?: string;
  productPrice: OfferProductPrice;
  selectedVariantId?: number;
  sizeName?: string;
  predefinedOfferPrice?: string;
};

const EMPTY_ERROR = {
  code: -1, // an error code which doesn't exist
  id: '',
  message: '',
  request_id: '',
};

export function MakeOfferModalForm({
  currencySymbol,
  isAuthenticated,
  onOfferSent,
  productId,
  productPrice,
  productImageHref,
  selectedVariantId,
  sizeName,
  predefinedOfferPrice,
}: Props) {
  const qc = useQueryClient();
  const tOffers = useTranslations('offers');
  const { sendSuggestedOffersActionEvent, sendMakeOfferFormSubmitEvents } =
    useOffersTracking();
  const [selectedSuggestedPrice, setSelectedSuggestedPrice] = useState<
    number | undefined
  >(undefined);
  const [priceQuote, setPriceQuote] = useState<OfferPriceQuoteResponse | null>(
    null
  );
  const [error, setError] = useState<OfferErrorResponse>(EMPTY_ERROR);
  const [inputDirty, setInputDirty] = useState(false);
  const [offerPrice, setOfferPrice] = useState(predefinedOfferPrice || '');
  const debouncedOfferPrice = useDebounce(
    offerPrice,
    DEBOUNCE_PRICE_QUOTE_DELAY
  );
  const debouncedOfferValid = isValidOfferValue(debouncedOfferPrice);

  function handleSelectedPriceClick({
    event,
    value,
    index,
  }: SuggestionOptionSelection) {
    event.preventDefault();
    sendSuggestedOffersActionEvent(productId, value, productPrice.currencyName);

    setError(EMPTY_ERROR);
    setOfferPrice(value);
    setSelectedSuggestedPrice(index);
  }

  function handleOfferPriceChange(e: ChangeEvent<HTMLInputElement>) {
    setInputDirty(true);
    setError(EMPTY_ERROR);
    setSelectedSuggestedPrice(undefined);
    setOfferPrice(e.target.value);
  }

  async function handleOfferRequestError(
    errorRes: AxiosCompatibleError<OfferErrorResponse>
  ) {
    const error = errorRes.response?.data;

    if (!error?.id) {
      return setError({
        code: 0,
        id: GENERIC_ERROR_ID,
        message: '',
        request_id: errorRes.response?.data?.request_id ?? '',
      });
    }
    setError(error);
  }

  function logUpfrontMetric(components?: HeadlinePriceComponents) {
    const regionCode = qc
      .getQueryData<string>([RQ_X_REGION_CODE])
      ?.toLowerCase();
    const ua = navigator.userAgent;

    if (regionCode === 'ct' || regionCode === 'ny') {
      if (
        ua &&
        !ua.toLocaleLowerCase().includes('google') &&
        !isWebCrawler(ua)
      ) {
        const displayState =
          components?.buyer_fee && components?.shipping_price
            ? 'shown'
            : 'not_shown';
        recordMetric({
          type: 'increment',
          stat: Stdv1Stat[
            `offers.${regionCode}.upfront_shipping_${displayState}`
          ],
        });
      }
    }
  }

  function getErrorText(error: OfferErrorResponse) {
    if (!error.id) {
      return undefined;
    }

    if (error.id === 'OFFER_BELOW_FLOOR_PRICE') {
      return tOffers(`Error.${error.id}`, {
        floorPercentage: error.payload?.action || '',
      });
    }

    if (OFFER_ERROR_TRANSLATION_KEYS.includes(error.id)) {
      return tOffers(`Error.${error.id}`);
    }

    return tOffers(`Error.${GENERIC_ERROR_ID}`);
  }

  const errorText = getErrorText(error);

  const createOfferMutation = useMutation({
    mutationFn: (body: CreateOfferRequest) => createOffer(body),
    onSuccess: () => {
      onOfferSent();
      qc.invalidateQueries({ queryKey: [RQ_BUYER_ACTIVE_OFFERS] });
    },
    onError: (error: AxiosCompatibleError<OfferErrorResponse>) =>
      handleOfferRequestError(error),
  });

  const priceQuoteMutation = useMutation({
    mutationFn: (body: PriceQuoteRequestBody) => createOfferPriceQuote(body),
    onSuccess: (res) => {
      const { data } = res;
      logUpfrontMetric(data.quote_price_components);
      setPriceQuote(data);
    },
    onError: (error: AxiosCompatibleError<OfferErrorResponse>) => {
      setPriceQuote(null);
      handleOfferRequestError(error);
    },
  });

  const hasClearedInput = !offerPrice && inputDirty;

  const isPriceQuoteVisible =
    Boolean(priceQuote) &&
    !errorText &&
    !hasClearedInput &&
    !priceQuoteMutation.isPending &&
    debouncedOfferValid;

  const isRequestPending =
    createOfferMutation.isPending || priceQuoteMutation.isPending;

  const isSubmitDisabled =
    !isValidOfferValue(offerPrice) ||
    isRequestPending ||
    // We still want to enable the button if there is a generic API request error
    // e.g. the Mobile API is down, which is why we're checking for an error id
    Boolean(error?.id);

  async function handleSubmit(e: React.SubmitEvent<HTMLFormElement>) {
    e.preventDefault();

    sendMakeOfferFormSubmitEvents(productId, offerPrice, productPrice);

    createOfferMutation.mutate({
      product_id: productId,
      offer_value: offerPrice,
      offer_currency: productPrice.currencyName,
      ...(selectedVariantId ? { variant_id: selectedVariantId } : {}),
    });
  }

  useEffect(() => {
    if (debouncedOfferValid) {
      priceQuoteMutation.mutate({
        product_id: productId,
        offer_value: debouncedOfferPrice,
        offer_currency: productPrice.currencyName,
        variant_id: selectedVariantId,
      });
    }
  }, [
    debouncedOfferPrice,
    isAuthenticated,
    productId,
    productPrice.currencyName,
    selectedVariantId,
  ]);

  return (
    <form onSubmit={handleSubmit}>
      <OfferModalProductSummary
        imageHref={productImageHref}
        currencySymbol={currencySymbol}
        productPrice={productPrice}
        sizeName={sizeName}
      />
      <OfferModalInput
        currencySymbol={currencySymbol}
        errorText={errorText}
        isLoading={priceQuoteMutation.isPending}
        labelText={tOffers('OfferAmountLabel')}
        offerPrice={offerPrice}
        onChange={handleOfferPriceChange}
        placeholder={productPrice.baseCurrentPrice}
        quote={isPriceQuoteVisible ? priceQuote?.quote : undefined}
        quotePriceComponents={priceQuote?.quote_price_components}
      />
      <SuggestedOfferPriceButtons
        baseCurrentPrice={parseFloat(productPrice.baseCurrentPrice)}
        selectedSuggestedPrice={selectedSuggestedPrice}
        onSuggestedPriceClick={handleSelectedPriceClick}
        currencySymbol={currencySymbol}
        productId={productId}
      />
      <div className={styles.submitButton}>
        <ButtonExperiment
          fullWidth
          disabled={isSubmitDisabled}
          isLoading={createOfferMutation.isPending}
          type="submit"
        >
          {tOffers('SendOffer')}
        </ButtonExperiment>
      </div>
      <div className={styles.timeWrapper}>
        <TimerCountdownIcon size={18} />
        <Caption variant="sm-regular">
          {tOffers('OfferTimelimitWarning')}
        </Caption>
      </div>
    </form>
  );
}
